/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  Coordinates,
  Coordinates$inboundSchema,
  Coordinates$Outbound,
  Coordinates$outboundSchema,
} from "./coordinates.js";
import {
  LabelSumRef,
  LabelSumRef$inboundSchema,
  LabelSumRef$Outbound,
  LabelSumRef$outboundSchema,
} from "./labelsumref.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";

/**
 * The variant of the label. Client determines display, could vary on the theme.
 */
export const LabelConfigVariant = {
  Primary: "primary",
  Secondary: "secondary",
  Disabled: "disabled",
  Error: "error",
  Warning: "warning",
  Caution: "caution",
  Critical: "critical",
  EstoppedBanner: "estopped_banner",
  FaultedBanner: "faulted_banner",
} as const;
/**
 * The variant of the label. Client determines display, could vary on the theme.
 */
export type LabelConfigVariant = OpenEnum<typeof LabelConfigVariant>;

/**
 * Defines a label that is overlaid on top of an animatic.
 */
export type LabelConfig = {
  /**
   * The coordinates of an object on the animatic.
   */
  coordinates?: Coordinates | undefined;
  /**
   * The description of the label
   */
  description?: string | undefined;
  /**
   * The display units of the label
   */
  displayUnits?: string | undefined;
  /**
   * Whether the label is a banner. If true, the label will be displayed as a banner.
   */
  isBanner?: boolean | undefined;
  /**
   * A reference to a reading or setting.
   */
  ref?: Ref | undefined;
  /**
   * The summable references for complex labels
   */
  refSum?: Array<LabelSumRef> | undefined;
  /**
   * The constant value of the label
   */
  value?: string | undefined;
  /**
   * The variant of the label. Client determines display, could vary on the theme.
   */
  variant?: LabelConfigVariant | undefined;
};

/** @internal */
export const LabelConfigVariant$inboundSchema: z.ZodType<
  LabelConfigVariant,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(LabelConfigVariant);
/** @internal */
export const LabelConfigVariant$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  LabelConfigVariant
> = openEnums.outboundSchema(LabelConfigVariant);

/** @internal */
export const LabelConfig$inboundSchema: z.ZodType<
  LabelConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  coordinates: types.optional(Coordinates$inboundSchema),
  description: types.optional(types.string()),
  display_units: types.optional(types.string()),
  is_banner: types.boolean().default(false),
  ref: types.optional(Ref$inboundSchema),
  ref_sum: types.optional(z.array(LabelSumRef$inboundSchema)),
  value: types.optional(types.string()),
  variant: LabelConfigVariant$inboundSchema.default("primary"),
}).transform((v) => {
  return remap$(v, {
    "display_units": "displayUnits",
    "is_banner": "isBanner",
    "ref_sum": "refSum",
  });
});
/** @internal */
export type LabelConfig$Outbound = {
  coordinates?: Coordinates$Outbound | undefined;
  description?: string | undefined;
  display_units?: string | undefined;
  is_banner: boolean;
  ref?: Ref$Outbound | undefined;
  ref_sum?: Array<LabelSumRef$Outbound> | undefined;
  value?: string | undefined;
  variant: string;
};

/** @internal */
export const LabelConfig$outboundSchema: z.ZodType<
  LabelConfig$Outbound,
  z.ZodTypeDef,
  LabelConfig
> = z.object({
  coordinates: Coordinates$outboundSchema.optional(),
  description: z.string().optional(),
  displayUnits: z.string().optional(),
  isBanner: z.boolean().default(false),
  ref: Ref$outboundSchema.optional(),
  refSum: z.array(LabelSumRef$outboundSchema).optional(),
  value: z.string().optional(),
  variant: LabelConfigVariant$outboundSchema.default("primary"),
}).transform((v) => {
  return remap$(v, {
    displayUnits: "display_units",
    isBanner: "is_banner",
    refSum: "ref_sum",
  });
});

export function labelConfigToJSON(labelConfig: LabelConfig): string {
  return JSON.stringify(LabelConfig$outboundSchema.parse(labelConfig));
}
export function labelConfigFromJSON(
  jsonString: string,
): SafeParseResult<LabelConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LabelConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LabelConfig' from JSON`,
  );
}
